import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import LunchDiningOutlinedIcon from '@mui/icons-material/LunchDiningOutlined';
import BedtimeOutlinedIcon from '@mui/icons-material/BedtimeOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import LocalDrinkOutlinedIcon from '@mui/icons-material/LocalDrinkOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { getAdaptiveCareLensKey } from './adaptiveQuickLogConfig';

const DEFAULT_MEAL_TYPES = [
  { label: 'Breakfast', iconComponent: WbSunnyOutlinedIcon, bg: '#FFF8EC', border: '#E6D7B0', iconBg: '#FFF1D2', ink: '#6B4C1E' },
  { label: 'Lunch', iconComponent: LunchDiningOutlinedIcon, bg: '#EAF2EE', border: '#9CC6B8', iconBg: '#DCEAE3', ink: '#2E5B4B' },
  { label: 'Dinner', iconComponent: BedtimeOutlinedIcon, bg: '#EAE8F4', border: '#B7B0D6', iconBg: '#DDD9EC', ink: '#514A73' },
  { label: 'Snack', iconComponent: RestaurantOutlinedIcon, bg: '#F6DED2', border: '#E2A78D', iconBg: '#F3D0BE', ink: '#7A4A2F' },
  { label: 'Drink', iconComponent: LocalDrinkOutlinedIcon, bg: '#E8F0F8', border: '#B7CDE6', iconBg: '#DCE9F4', ink: '#385A78' },
  { label: 'Other', iconComponent: AddOutlinedIcon, bg: '#F0F0F0', border: '#D2D8D3', iconBg: '#E5E8E6', ink: '#5D6661' },
];

const HOSPICE_FOOD_TYPES = [
  { label: 'Food', iconComponent: RestaurantOutlinedIcon, bg: '#FFF8EC', border: '#E6D7B0', iconBg: '#FFF1D2', ink: '#6B4C1E' },
  { label: 'Drink', iconComponent: LocalDrinkOutlinedIcon, bg: '#E8F0F8', border: '#B7CDE6', iconBg: '#DCE9F4', ink: '#385A78' },
  { label: 'Small snack', iconComponent: LunchDiningOutlinedIcon, bg: '#EAF2EE', border: '#9CC6B8', iconBg: '#DCEAE3', ink: '#2E5B4B' },
  { label: 'Mouth care', iconComponent: AddOutlinedIcon, bg: '#F0F0F0', border: '#D2D8D3', iconBg: '#E5E8E6', ink: '#5D6661' },
  { label: 'Other', iconComponent: AddOutlinedIcon, bg: '#F0F0F0', border: '#D2D8D3', iconBg: '#E5E8E6', ink: '#5D6661' },
];

const DEFAULT_PORTION_OPTIONS = [
  { label: 'All of it', value: 'All of it' },
  { label: 'Most of it', value: 'Most of it' },
  { label: 'Half', value: 'Half' },
  { label: 'A few bites', value: 'A few bites' },
  { label: 'Refused', value: 'Refused' },
];

const HOSPICE_AMOUNT_OPTIONS = [
  { label: 'A good amount', value: 'Good amount' },
  { label: 'A few bites or sips', value: 'A few bites' },
  { label: 'Tasted only', value: 'Tasted only' },
  { label: 'Not interested today', value: 'Not interested today' },
];

const DEFAULT_REACTION_OPTIONS = [
  { label: 'None', value: 'None', bg: '#F0F0F0', border: '#D9DED9', ink: '#5F6B66' },
  { label: 'Spit out', value: 'Spit out', bg: '#F6DED2', border: '#E3B69F', ink: '#7A4A2F' },
  { label: 'Gagged', value: 'Gagged', bg: '#EAE8F4', border: '#C6BFE2', ink: '#514A73' },
  { label: 'Refused', value: 'Refused', bg: '#FEF2F2', border: '#F9BABA', ink: '#B45050' },
  { label: 'Allergy concern', value: 'Allergy concern', bg: '#FEF2F2', border: '#F9BABA', ink: '#B45050' },
  { label: 'Upset stomach', value: 'Upset stomach', bg: '#E8F0F8', border: '#BED2EA', ink: '#385A78' },
];

const HOSPICE_COMFORT_OPTIONS = [
  { label: 'No concern noticed', value: 'None', bg: '#F0F0F0', border: '#D9DED9', ink: '#5F6B66' },
  { label: 'Comfortable', value: 'Comfortable', bg: '#EAF2EE', border: '#9CC6B8', ink: '#2E5B4B' },
  { label: 'Swallowing concern', value: 'Swallowing concern', bg: '#FEF2F2', border: '#F9BABA', ink: '#B45050' },
  { label: 'Nausea', value: 'Nausea', bg: '#F6DED2', border: '#E3B69F', ink: '#7A4A2F' },
  { label: 'Mouth discomfort', value: 'Mouth discomfort', bg: '#EAE8F4', border: '#C6BFE2', ink: '#514A73' },
  { label: 'Not sure', value: 'Not sure', bg: '#F0F0F0', border: '#D9DED9', ink: '#5F6B66' },
];

const DEFAULT_FOOD_LOG_CONFIG = {
  title: 'Food',
  saveLabel: 'Save food log',
  savingLabel: 'Saving...',
  fallbackText: 'Food logged',
  typeSectionLabel: 'Meal type',
  eatenSectionLabel: 'What was eaten?',
  eatenPlaceholder: 'Describe the food, portions, and context',
  amountSectionLabel: 'How much?',
  reactionSectionLabel: 'Any reactions?',
  notesLabel: 'Notes',
  notesPlaceholder: 'Any additional context',
  mealTypes: DEFAULT_MEAL_TYPES,
  portionOptions: DEFAULT_PORTION_OPTIONS,
  reactionOptions: DEFAULT_REACTION_OPTIONS,
  allergyWarningTitle: 'Allergy concern flagged',
  allergyWarningBody: 'This log will be marked for caregiver review',
};

const FOOD_LOG_CONFIG_BY_LENS = {
  hospice: {
    ...DEFAULT_FOOD_LOG_CONFIG,
    title: 'Food & Drink',
    saveLabel: 'Save food & drink',
    fallbackText: 'Food and drink logged',
    typeSectionLabel: 'What was offered?',
    eatenSectionLabel: 'What was taken or enjoyed?',
    eatenPlaceholder: 'Food, drink, mouth care, favorite tastes, or what seemed comforting',
    amountSectionLabel: 'How much was taken?',
    reactionSectionLabel: 'Comfort while eating or drinking',
    notesLabel: 'Notes',
    notesPlaceholder: 'Swallowing, nausea, mouth comfort, favorite foods, what they enjoyed...',
    mealTypes: HOSPICE_FOOD_TYPES,
    portionOptions: HOSPICE_AMOUNT_OPTIONS,
    reactionOptions: HOSPICE_COMFORT_OPTIONS,
  },
};

export const getAdaptiveFoodLogConfig = (child = {}) => (
  FOOD_LOG_CONFIG_BY_LENS[getAdaptiveCareLensKey(child)] || DEFAULT_FOOD_LOG_CONFIG
);

const getFoodEntryLensKey = (entry = {}) => (
  entry?.adaptiveFoodLogLens
  || entry?.foodDetails?.adaptiveFoodLogLens
  || ''
);

export const resolveAdaptiveFoodLogPresentation = ({ entry = {}, child = null } = {}) => {
  const entryLensKey = getFoodEntryLensKey(entry);
  const config = entryLensKey
    ? (FOOD_LOG_CONFIG_BY_LENS[entryLensKey] || DEFAULT_FOOD_LOG_CONFIG)
    : getAdaptiveFoodLogConfig(child || {});
  const title = entry?.adaptiveFoodLogLabel || entry?.foodDetails?.adaptiveFoodLogLabel || config.title || DEFAULT_FOOD_LOG_CONFIG.title;

  return {
    lensKey: entryLensKey || getAdaptiveCareLensKey(child || {}),
    categoryLabel: title,
    title,
    titlePrefix: title,
    amountLabel: config.amountSectionLabel || DEFAULT_FOOD_LOG_CONFIG.amountSectionLabel,
    reactionLabel: config.reactionSectionLabel || DEFAULT_FOOD_LOG_CONFIG.reactionSectionLabel,
    notesLabel: config.notesLabel || DEFAULT_FOOD_LOG_CONFIG.notesLabel,
    fallbackText: config.fallbackText || DEFAULT_FOOD_LOG_CONFIG.fallbackText,
  };
};
